import type { NamespaceCountriesTranslation } from '../locale/i18n-types';

export type PhonePrefixConfig = {
	prefix: string;
	countryCode: keyof NamespaceCountriesTranslation;
	minLength: number;
	maxLength: number;
};

export const PHONE_PREFIX_CONFIG = [
	{ prefix: '+420', countryCode: 'CZE', minLength: 9, maxLength: 9 },
	{ prefix: '+421', countryCode: 'SVK', minLength: 9, maxLength: 9 },
	{ prefix: '+355', countryCode: 'ALB', minLength: 8, maxLength: 9 },
	{ prefix: '+376', countryCode: 'AND', minLength: 6, maxLength: 6 },
	{ prefix: '+374', countryCode: 'ARM', minLength: 8, maxLength: 8 },
	{ prefix: '+994', countryCode: 'AZE', minLength: 9, maxLength: 9 },
	{ prefix: '+32', countryCode: 'BEL', minLength: 8, maxLength: 9 },
	{
		prefix: '+387',
		countryCode: 'BIH',
		minLength: 8,
		maxLength: 8,
	},
	{ prefix: '+359', countryCode: 'BGR', minLength: 8, maxLength: 9 },
	{ prefix: '+385', countryCode: 'HRV', minLength: 8, maxLength: 9 },
	{ prefix: '+45', countryCode: 'DNK', minLength: 8, maxLength: 8 },
	{ prefix: '+372', countryCode: 'EST', minLength: 7, maxLength: 8 },
	{ prefix: '+358', countryCode: 'FIN', minLength: 5, maxLength: 12 },
	{ prefix: '+33', countryCode: 'FRA', minLength: 9, maxLength: 9 },
	{ prefix: '+995', countryCode: 'GEO', minLength: 9, maxLength: 9 },
	{ prefix: '+353', countryCode: 'IRL', minLength: 7, maxLength: 9 },
	{ prefix: '+354', countryCode: 'ISL', minLength: 7, maxLength: 7 },
	{ prefix: '+39', countryCode: 'ITA', minLength: 6, maxLength: 11 },
	{ prefix: '+383', countryCode: 'KSV', minLength: 8, maxLength: 9 },
	{ prefix: '+357', countryCode: 'CYP', minLength: 8, maxLength: 8 },
	{
		prefix: '+423',
		countryCode: 'LIE',
		minLength: 7,
		maxLength: 7,
	},
	{ prefix: '+370', countryCode: 'LTU', minLength: 8, maxLength: 8 },
	{ prefix: '+371', countryCode: 'LVA', minLength: 8, maxLength: 8 },
	{ prefix: '+352', countryCode: 'LUX', minLength: 4, maxLength: 11 },
	{ prefix: '+36', countryCode: 'HUN', minLength: 8, maxLength: 9 },
	{ prefix: '+356', countryCode: 'MLT', minLength: 8, maxLength: 8 },
	{ prefix: '+373', countryCode: 'MDA', minLength: 8, maxLength: 8 },
	{ prefix: '+377', countryCode: 'MCO', minLength: 8, maxLength: 8 },
	{ prefix: '+49', countryCode: 'DEU', minLength: 6, maxLength: 13 },
	{ prefix: '+31', countryCode: 'NLD', minLength: 9, maxLength: 9 },
	{ prefix: '+47', countryCode: 'NOR', minLength: 8, maxLength: 8 },
	{ prefix: '+48', countryCode: 'POL', minLength: 9, maxLength: 9 },
	{ prefix: '+351', countryCode: 'PRT', minLength: 9, maxLength: 9 },
	{ prefix: '+43', countryCode: 'AUT', minLength: 4, maxLength: 13 },
	{ prefix: '+40', countryCode: 'ROU', minLength: 9, maxLength: 9 },
	{ prefix: '+30', countryCode: 'GRC', minLength: 10, maxLength: 10 },
	{ prefix: '+378', countryCode: 'SMR', minLength: 6, maxLength: 10 },
	{
		prefix: '+389',
		countryCode: 'MKD',
		minLength: 8,
		maxLength: 8,
	},
	{ prefix: '+386', countryCode: 'SVN', minLength: 8, maxLength: 8 },
	{
		prefix: '+44',
		countryCode: 'GBR',
		minLength: 10,
		maxLength: 10,
	},
	{ prefix: '+381', countryCode: 'SRB', minLength: 8, maxLength: 9 },
	{ prefix: '+34', countryCode: 'ESP', minLength: 9, maxLength: 9 },
	{ prefix: '+46', countryCode: 'SWE', minLength: 7, maxLength: 13 },
	{ prefix: '+41', countryCode: 'CHE', minLength: 9, maxLength: 9 },
	{ prefix: '+90', countryCode: 'TUR', minLength: 10, maxLength: 10 },
	{ prefix: '+380', countryCode: 'UKR', minLength: 9, maxLength: 9 },
	{ prefix: '+382', countryCode: 'MNE', minLength: 8, maxLength: 8 },
] as const satisfies readonly PhonePrefixConfig[];

export type ExtendedPhonePrefixEnumType =
	(typeof PHONE_PREFIX_CONFIG)[number]['prefix'];

export const EXTENDED_PHONE_PREFIXES = PHONE_PREFIX_CONFIG.map(
	({ prefix }) => prefix,
);

type PhonePrefixRule = { min: number; max: number };

export const PHONE_PREFIX_RULES: ReadonlyMap<
	ExtendedPhonePrefixEnumType,
	PhonePrefixRule
> = new Map(
	PHONE_PREFIX_CONFIG.map(
		({ prefix, minLength, maxLength }) =>
			[prefix, { min: minLength, max: maxLength }] as const,
	),
);

export const DEFAULT_PHONE_PREFIX: ExtendedPhonePrefixEnumType =
	PHONE_PREFIX_CONFIG[0].prefix;

export const checkIsExtendedPhonePrefix = (
	prefix: string,
): prefix is ExtendedPhonePrefixEnumType =>
	PHONE_PREFIX_CONFIG.some(({ prefix: knownPrefix }) => knownPrefix === prefix);

export const getExtendedPhonePrefix = (
	prefix: string | undefined,
): ExtendedPhonePrefixEnumType =>
	prefix && checkIsExtendedPhonePrefix(prefix) ? prefix : DEFAULT_PHONE_PREFIX;
